import { DOCUMENT, Injectable, inject } from '@angular/core';
import {
  LIB_CONFIG,
  RestService,
  SecureStorageService,
} from '@copec-gid/gid-arquitectura.seguridad-autorizacion-modulo';
import { firstValueFrom } from 'rxjs';

import { ExternalNavigation } from '../navigation/external-navigation';
import { parseUrl } from '../navigation/parse-url';
import { RETURN_PARAM, toReturnUrl } from '../navigation/return-path';
import { AuthSession, type AuthState } from './auth-session';

export const TUNNEL_ENDPOINTS = { session: 'sesion', authorize: 'authorize' } as const;

export const MAX_SESSION_ATTEMPTS = 3;

export const TUNNEL_KEYS = {
  token: 'session_token',
  key: 'session_key',
  level: 'level_id',
  signInPending: 'sign_in_pending',
} as const;

function readString(source: unknown, names: readonly string[]): string | null {
  if (source === null || typeof source !== 'object') {
    return null;
  }

  for (const name of names) {
    const value: unknown = Reflect.get(source, name);
    if (typeof value === 'string' && value !== '') {
      return value;
    }
  }

  return null;
}

function readField(response: unknown, ...names: string[]): string | null {
  const data: unknown = response !== null && typeof response === 'object' ? Reflect.get(response, 'data') : null;
  return readString(data, names) ?? readString(response, names);
}

function isSecureRedirect(url: string): boolean {
  return parseUrl(url)?.protocol === 'https:';
}

@Injectable({ providedIn: 'root' })
export class TunnelAuthSession extends AuthSession {
  private readonly rest = inject(RestService);
  private readonly storage = inject(SecureStorageService);
  private readonly config = inject(LIB_CONFIG);
  private readonly navigation = inject(ExternalNavigation);
  private readonly document = inject(DOCUMENT);
  private renewal: Promise<void> | null = null;

  restore(): Promise<AuthState> {
    return Promise.resolve(this.read(TUNNEL_KEYS.key) === null ? 'unauthenticated' : 'authenticated');
  }

  async signIn(returnTo: string): Promise<void> {
    const reusesTunnel = this.read(TUNNEL_KEYS.key) !== null;
    if (!reusesTunnel) {
      await this.openTunnel();
    }

    let response: unknown;
    try {
      response = await this.authorize(returnTo);
    } catch (error) {
      if (!reusesTunnel) {
        throw error;
      }
      this.clearTunnel();
      await this.openTunnel();
      response = await this.authorize(returnTo);
    }

    const redirectUri = readField(response, 'redirect_uri');
    if (redirectUri === null || !isSecureRedirect(redirectUri)) {
      throw new Error('authorize did not return a secure redirect_uri');
    }

    this.storage.setItem(this.name(TUNNEL_KEYS.signInPending), 'true', true);
    this.navigation.open(redirectUri);
  }

  renewTunnel(): Promise<void> {
    this.renewal ??= this.reopenTunnel().finally(() => {
      this.renewal = null;
    });
    return this.renewal;
  }

  signInPending(): boolean {
    return this.storage.getSignal<string>(this.name(TUNNEL_KEYS.signInPending), true)() !== null;
  }

  clearPendingSignIn(): void {
    this.storage.clearItem(this.name(TUNNEL_KEYS.signInPending), true);
  }

  private authorize(returnTo: string): Promise<unknown> {
    return firstValueFrom(
      this.rest.get<unknown>(TUNNEL_ENDPOINTS.authorize, {
        [RETURN_PARAM]: toReturnUrl(returnTo, this.document.location.origin),
      }),
    );
  }

  private async openTunnel(): Promise<void> {
    const response = await firstValueFrom(
      this.rest.post<unknown>(TUNNEL_ENDPOINTS.session, { sesion_tipo: 'sesion_crear' }, { isInitSession: true }),
    );
    const token = readField(response, 'session_token', 'sesion_token');
    const key = readField(response, 'session_key', 'sesion_key');
    if (key === null) {
      throw new Error('sesion did not return the tunnel key');
    }

    if (token !== null) {
      this.storage.setItem(this.name(TUNNEL_KEYS.token), token);
    }
    this.storage.setItem(this.name(TUNNEL_KEYS.key), key);
    this.storage.setItem(this.name(TUNNEL_KEYS.level), this.config.levelid ?? '');
  }

  private async reopenTunnel(): Promise<void> {
    this.clearTunnel();
    await this.openTunnelWithRetries(MAX_SESSION_ATTEMPTS);
  }

  private async openTunnelWithRetries(attemptsLeft: number): Promise<void> {
    try {
      await this.openTunnel();
    } catch (error) {
      if (attemptsLeft <= 1) {
        throw error;
      }
      await this.openTunnelWithRetries(attemptsLeft - 1);
    }
  }

  private clearTunnel(): void {
    for (const key of [TUNNEL_KEYS.token, TUNNEL_KEYS.key, TUNNEL_KEYS.level]) {
      this.storage.clearItem(this.name(key));
    }
  }

  private read(key: string): string | null {
    return this.storage.getSignal<string>(this.name(key))();
  }

  private name(key: string): string {
    return this.storage.encrypt(key, true);
  }
}
